
export class MidiManager {
    constructor() {
        this.midiAccess = null;
        this.inputs = new Map();
        
        // Callbacks
        this.onNoteOn = (note, velocity) => {};
        this.onNoteOff = (note) => {};
        this.onControlChange = (cc, value) => {};
        this.onPitchBend = (value) => {}; // Value: -1.0 to 1.0
        this.onChannelPressure = (pressure) => {};
        this.onProgramChange = (program) => {};
        
        // System Real-Time
        this.onClock = () => {};
        this.onStart = () => {};
        this.onContinue = () => {};
        this.onStop = () => {};
    }

    async init() {
        if (navigator.requestMIDIAccess) {
            try {
                this.midiAccess = await navigator.requestMIDIAccess();
                
                this.setupInputs();
                
                this.midiAccess.onstatechange = (e) => {
                    if (e.port.state === 'connected' && e.port.type === 'input') {
                        e.port.onmidimessage = this.handleMidiMessage.bind(this);
                    }
                };
                return true;
            } catch (err) {
                console.warn("Web MIDI Init Failed:", err);
            }
        } 
        
        // Fallback: Tauri Bridge
        if (window.__TAURI__) {
            console.log("Attempting Tauri MIDI Bridge...");
            try {
                // Tauri v2 uses .core, v1 used .tauri
                const tauriModule = window.__TAURI__.core || window.__TAURI__.tauri;
                const invoke = tauriModule?.invoke;
                const eventModule = window.__TAURI__.event;
                const listen = eventModule?.listen;

                if (!invoke || !listen) {
                    throw new Error("Tauri API (invoke/listen) not found. Check global configuration.");
                }
                
                const msg = await invoke('init_midi');
                console.log("Tauri MIDI:", msg);
                
                await listen('midi-message', (event) => {
                    // Event payload: [data_array, timestamp]
                    // We just need data
                    const [data, timestamp] = event.payload;
                    // Mock an event object similar to Web MIDI
                    this.handleMidiMessage({ data: new Uint8Array(data), timeStamp: timestamp });
                });
                return true;
            } catch (e) {
                console.warn("Tauri MIDI Init Failed:", e);
            }
        } else {
            console.warn("Web MIDI API not supported in this browser.");
        }
        
        return false;
    }

    setupInputs() {
        // Clear old listeners? MIDI inputs are iterators.
        for (let input of this.midiAccess.inputs.values()) {
            input.onmidimessage = this.handleMidiMessage.bind(this);
            if (!this.inputs.has(input.id)) {
                this.inputs.set(input.id, input);
                console.log(`MIDI Input Connected: ${input.name}`);
            }
        }
    }

    handleMidiMessage(event) {
        const [status, data1, data2] = event.data;
        const command = status & 0xf0;
        const channel = status & 0x0f;

        // Note On (144)
        if (command === 144) {
            const note = data1;
            const velocity = data2;
            if (velocity > 0) {
                this.onNoteOn(note, velocity);
            } else {
                // Velocity 0 is often used as Note Off
                this.onNoteOff(note);
            }
        }
        // Note Off (128)
        else if (command === 128) {
            const note = data1;
            this.onNoteOff(note);
        }
        // Control Change (176)
        else if (command === 176) {
            const cc = data1;
            const value = data2;
            this.onControlChange(cc, value);
        }
        // Program Change (192)
        else if (command === 192) {
            const program = data1;
            this.onProgramChange(program);
        }
        // Channel Pressure (208)
        else if (command === 208) {
            const pressure = data1;
            this.onChannelPressure(pressure);
        }
        // Pitch Bend (224)
        else if (command === 224) {
            const lsb = data1;
            const msb = data2;
            // 14-bit value: 0 - 16383. Center: 8192
            const val = (msb << 7) | lsb;
            // Normalize to -1.0 to 1.0
            const normalized = (val - 8192) / 8192;
            this.onPitchBend(normalized);
        }
        // System Real-Time
        else if (status === 248) { // 0xF8 Clock
            this.onClock();
        }
        else if (status === 250) { // 0xFA Start
            this.onStart();
        }
        else if (status === 251) { // 0xFB Continue
            this.onContinue();
        }
        else if (status === 252) { // 0xFC Stop
            this.onStop();
        }
    }
}
